import React, { useCallback, useEffect } from 'react';
import { X, Feather, Heart, Handshake, Compass, ShieldCheck, ArrowUpRight } from 'lucide-react';

const StoryHeader = ({ onClose }) => (
  <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-[color:var(--green)] flex items-center justify-center">
        <Feather className="w-4 h-4 text-[color:var(--obsidian)]" />
      </div>
      <div>
        <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/60">Our Story · Est. 1994</div>
        <div className="font-display text-[22px] tracking-tight text-[color:var(--parchment)]">The people behind Ionic</div>
      </div>
    </div>
    <button
      onClick={onClose}
      data-testid="story-close-btn"
      className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-[color:var(--parchment)] hover:border-[color:var(--green)] hover:text-[color:var(--green)] transition-colors"
      aria-label="Close our story"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
);

const PULL_QUOTE =
  "We've all felt the weight of life's hardest chapters — our own, our family's, our friends'. We adapted. We overcame. And we learned to guide others the way we wished someone had guided us.";

const PILLARS = [
  {
    id: 'empathy',
    icon: Heart,
    label: 'Empathy first',
    body: 'Every estate is a chapter closing. We move at the pace the family needs — never the other way around.',
  },
  {
    id: 'honesty',
    icon: ShieldCheck,
    label: 'Honesty in every price',
    body: 'Fair, researched, transparent. If we would not accept the number ourselves, we would not offer it to you.',
  },
  {
    id: 'craft',
    icon: Handshake,
    label: 'Care for the pieces',
    body: 'The way a home is staged and handed off says everything about how it was loved. We treat every item like it matters — because it does.',
  },
  {
    id: 'follow-through',
    icon: Compass,
    label: 'The follow-through',
    body: 'A signed proposal is a promise. Consultation, preparation, sale, cleanout — start to finish, on time, in writing.',
  },
];

const StoryBody = () => (
  <div className="flex-1 overflow-y-auto relative">
    {/* Blueprint background — subtle, atmospheric */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
    >
      <img
        src="/our-story-blueprint.png"
        alt=""
        className="w-full h-full object-cover object-center opacity-[0.14] mix-blend-screen"
      />
      {/* Darkening overlays for text legibility */}
      <div className="absolute inset-0 bg-[color:var(--obsidian-2)]/78" />
      <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--obsidian-2)]/70 via-[color:var(--obsidian-2)]/55 to-[color:var(--obsidian-2)]/85" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,10,10,0.55)_75%,rgba(10,10,10,0.9)_100%)]" />
    </div>

    <div className="relative">
    <div className="px-6 md:px-10 pt-8 md:pt-10 pb-6">
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
        Chapter One · Where We Started
      </div>
      <h2 className="font-display text-[30px] md:text-[40px] leading-[1.05] text-[color:var(--parchment)]">
        A quiet promise, kept for<br />
        <span className="text-[color:var(--green)]">three decades.</span>
      </h2>

      <blockquote className="mt-8 pl-5 border-l-2 border-[color:var(--green)]/70">
        <p className="font-display text-[19px] md:text-[22px] leading-[1.35] text-[color:var(--parchment)]/90 italic">
          &ldquo;{PULL_QUOTE}&rdquo;
        </p>
      </blockquote>
    </div>

    <div className="px-6 md:px-10 pb-2 space-y-6 text-[15px] md:text-[16px] leading-relaxed text-[color:var(--parchment)]/78">
      <p>
        Ionic did not start in a boardroom. It started at kitchen tables and in garages — on
        weekends and long evenings — with a small circle of people from very different walks of
        life who shared one belief: that showing up with positive energy and honest work could
        change someone&rsquo;s hardest day.
      </p>
      <p>
        We had each lived through the moments most families dread. A parent&rsquo;s passing. A
        move into memory care. A downsize after forty years in one home. We knew, first-hand,
        how disorienting it feels to stand in a doorway and not know where to begin. So we
        decided to become the people we had needed in those rooms — patient, calm, and quietly
        capable.
      </p>
    </div>

    <div className="px-6 md:px-10 pt-8">
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-3">
        Chapter Two · Why We Do It
      </div>
      <p className="text-[15px] md:text-[16px] leading-relaxed text-[color:var(--parchment)]/78">
        For most of our clients an estate sale is not a transaction — it is a threshold. What
        gets carried forward, what gets released, and how it feels on the other side matters
        long after the last box leaves the driveway. We treat every doorway we walk through the
        way we would want ours treated: with respect, with clarity, and with the dignity these
        moments deserve.
      </p>
    </div>

    <div className="px-6 md:px-10 pt-10">
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-3">
        Chapter Three · How We Show Up
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.id}
              data-testid={`story-pillar-${p.id}`}
              className="rounded-2xl border border-white/10 p-5 hover:border-[color:var(--green)]/50 transition-colors bg-[color:var(--obsidian)]/40"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full border border-[color:var(--green)]/50 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[color:var(--green)]" />
                </div>
                <div className="font-display text-[16px] text-[color:var(--parchment)] leading-tight">{p.label}</div>
              </div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[color:var(--parchment)]/70">{p.body}</p>
            </div>
          );
        })}
      </div>
    </div>

    <div className="px-6 md:px-10 pt-10 pb-2">
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-3">
        Chapter Four · Still, After All These Years
      </div>
      <p className="text-[15px] md:text-[16px] leading-relaxed text-[color:var(--parchment)]/78">
        Three decades in, we still remember what it felt like at the start — the nervousness,
        the trust extended by strangers, the quiet gratitude at the end of a job done well.
        That memory is what keeps our standards where they are. Every estate we handle is
        treated like the only one that matters. Because to the family standing in the doorway,
        it is.
      </p>
    </div>

    <div className="px-6 md:px-10 pt-10 pb-10">
      <div className="p-6 md:p-7 border border-[color:var(--green)]/40 rounded-2xl bg-[color:var(--green)]/[0.04]">
        <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-2">
          A note from the team
        </div>
        <p className="text-[14.5px] leading-relaxed text-[color:var(--parchment)]/80">
          If you are reading this while standing in a home full of decisions, take a breath. You
          do not have to figure it out alone. Reach out when you are ready — even if you are
          only ready to talk.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href="#sell"
            data-testid="story-cta-sell"
            className="btn-primary"
          >
            Start with a free consult <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <a
            href="#contact"
            data-testid="story-cta-contact"
            className="btn-outline"
          >
            Just say hello <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
    </div>
  </div>
);

const OurStoryDrawer = ({ open, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleClose]);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm transition-opacity duration-500 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside
        data-testid="our-story-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[520px] md:w-[640px] z-[80] bg-[color:var(--obsidian-2)] border-l border-white/10 transition-transform duration-[600ms] ${open ? 'translate-x-0' : 'translate-x-full'}`}
        aria-hidden={!open}
      >
        <div className="h-full flex flex-col">
          <StoryHeader onClose={handleClose} />
          <StoryBody />
          <div className="p-6 border-t border-white/10 text-[11px] font-mono tracking-[0.18em] uppercase text-[color:var(--parchment)]/50 text-center">
            Ionic Estate Sales · Oregon · Est. 1994
          </div>
        </div>
      </aside>
    </>
  );
};

export default OurStoryDrawer;
